import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Save, X, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full bg-white border hairline rounded-sm px-4 py-3 outline-none focus:border-gold text-sm";
const labelCls = "label-mono text-ink/60 block mb-2";
const BLANK = { title: "", category: "", excerpt: "", body: "" };

export default function AdminArticles() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // slug ou "new" ou null
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await axios.get(`${API}/articles`); setArticles(data); }
    catch { toast.error("Impossible de charger les articles."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startEdit = async (slug) => {
    try {
      const { data } = await axios.get(`${API}/articles/${slug}`);
      setForm({ title: data.title, category: data.category || "", excerpt: data.excerpt || "", body: data.body });
      setEditing(slug);
    } catch { toast.error("Article introuvable."); }
  };

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Titre et contenu requis.");
      return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        const { data } = await axios.post(`${API}/admin/articles`, form);
        toast.success("Article publié.", { description: `Disponible sur /blog/${data.slug}` });
      } else {
        await axios.put(`${API}/admin/articles/${editing}`, form);
        toast.success("Article mis à jour.");
      }
      setEditing(null);
      setForm(BLANK);
      load();
    } catch (e) {
      toast.error("Enregistrement impossible.", { description: e.response?.data?.detail || "Erreur serveur." });
    } finally { setSaving(false); }
  };

  const remove = async (slug) => {
    if (!window.confirm("Supprimer cet article définitivement ?")) return;
    try {
      await axios.delete(`${API}/admin/articles/${slug}`);
      toast.success("Article supprimé.");
      if (editing === slug) { setEditing(null); setForm(BLANK); }
      load();
    } catch { toast.error("Suppression impossible."); }
  };

  if (loading) return <div className="p-10 text-ink/50 label-mono" data-testid="articles-loading">Chargement…</div>;

  return (
    <div className="space-y-8" data-testid="admin-articles">
      <div className="flex items-center justify-between">
        <p className="text-ink/55 text-sm">{articles.length} article{articles.length > 1 ? "s" : ""} publié{articles.length > 1 ? "s" : ""} — visibles sur /blog et /blog/nom-article</p>
        <button onClick={() => { setForm(BLANK); setEditing("new"); }} data-testid="new-article-btn"
          className="inline-flex items-center gap-2 label-mono px-5 py-2.5 rounded-full bg-ink text-paper hover:bg-gold hover:text-ink transition-colors">
          <Plus size={14} /> Nouvel article
        </button>
      </div>

      {articles.length === 0 && !editing && (
        <div className="rounded-sm border hairline bg-white p-10 text-center" data-testid="articles-empty">
          <FileText size={28} className="mx-auto text-ink/25" />
          <p className="text-ink/55 text-sm mt-4">Aucun article publié pour le moment. Votre premier article apparaîtra en haut de la page Blog avec sa propre URL SEO.</p>
        </div>
      )}

      <div className="space-y-3">
        {articles.map((a) => (
          <div key={a.slug} className="rounded-sm border hairline bg-white p-5 flex flex-wrap items-center gap-4" data-testid={`article-row-${a.slug}`}>
            <div className="flex-1 min-w-[220px]">
              {a.category && <span className="label-mono text-gold">{a.category}</span>}
              <h4 className="font-display text-lg font-semibold leading-snug mt-1">{a.title}</h4>
              <p className="label-mono text-ink/40 mt-1.5">/blog/{a.slug} · {a.read_time} min · {new Date(a.published_at).toLocaleDateString("fr-FR")}</p>
            </div>
            <button onClick={() => startEdit(a.slug)} data-testid={`article-edit-${a.slug}`}
              className="inline-flex items-center gap-2 label-mono px-4 py-2 rounded-full border hairline hover:border-ink transition-colors"><Pencil size={13} /> Modifier</button>
            <button onClick={() => remove(a.slug)} aria-label="Supprimer" data-testid={`article-delete-${a.slug}`}
              className="w-9 h-9 rounded-full bg-ink/5 text-ink/50 flex items-center justify-center hover:bg-red-500 hover:text-white transition-colors"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>

      {editing && (
        <section className="rounded-sm border-2 border-gold/60 bg-white p-6 md:p-8 space-y-5" data-testid="article-editor">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-2xl font-semibold">{editing === "new" ? "Nouvel article" : "Modifier l'article"}</h3>
            <button onClick={() => { setEditing(null); setForm(BLANK); }} aria-label="Fermer" data-testid="article-cancel"
              className="w-9 h-9 rounded-full bg-ink/5 text-ink/50 flex items-center justify-center hover:bg-ink hover:text-paper transition-colors"><X size={15} /></button>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div><label className={labelCls}>Titre</label>
              <input className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="article-input-title" /></div>
            <div><label className={labelCls}>Catégorie (ex : Lean, TWI, Industrialisation)</label>
              <input className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="article-input-category" /></div>
          </div>
          <div><label className={labelCls}>Résumé (optionnel — sinon extrait automatiquement)</label>
            <textarea rows={2} className={inputCls} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} data-testid="article-input-excerpt" /></div>
          <div>
            <label className={labelCls}>Contenu — « ## Titre » pour une section, « - » pour une puce, ligne vide entre les paragraphes</label>
            <textarea rows={14} className={`${inputCls} font-mono`} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="article-input-body" />
          </div>
          <button onClick={save} disabled={saving} data-testid="article-save-btn"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold text-ink px-6 py-4 label-mono hover:bg-ink hover:text-paper transition-colors disabled:opacity-60">
            {saving ? <><Loader2 size={16} className="animate-spin" /> Publication…</> : <><Save size={15} /> {editing === "new" ? "Publier l'article" : "Enregistrer les modifications"}</>}
          </button>
        </section>
      )}
    </div>
  );
}
